// m7-station-browser.js — curated station catalog (ARD M7 contract).
// Exposes window.M7 = { init, getStations, getGenres, filterByGenre,
//                       getSurpriseRoster, getFavoritesRoster }.

window.M7 = (() => {
  let _catalog = [];
  let _loaded  = false;

  // ---------------------------------------------------------------------------
  // Public interface (ARD M7 contract)
  // ---------------------------------------------------------------------------

  /**
   * Fetches stations.json and stores the catalog in memory.
   * Returns a promise that resolves when the catalog is loaded.
   */
  const init = Logger.tracedAsync('M7', 'init', async () => {
    const res = await fetch('./stations.json');
    if (!res.ok) throw new Error(`Failed to load stations.json: ${res.status}`);
    _catalog = await res.json();
    _loaded = true;
    return { count: _catalog.length };
  });

  /**
   * Returns the full catalog array.
   */
  const getStations = Logger.traced('M7', 'getStations', () => {
    return [..._catalog];
  });

  /**
   * Returns a sorted array of unique genre strings from the catalog.
   */
  const getGenres = Logger.traced('M7', 'getGenres', () => {
    const genres = new Set();
    for (const s of _catalog) {
      if (s.genre) genres.add(s.genre);
    }
    return [...genres].sort();
  });

  /**
   * Returns stations matching the given genre.
   * Returns all stations if genre is null, undefined, or "All".
   */
  const filterByGenre = Logger.traced('M7', 'filterByGenre', (genre) => {
    if (!genre || genre === 'All') return [..._catalog];
    return _catalog.filter(s => s.genre === genre);
  });

  /**
   * Generates a roster of up to 10 stations with maximum genre diversity.
   * First pass: one random station per genre (genres shuffled).
   * Second pass: fill remaining slots randomly from unused stations.
   */
  const getSurpriseRoster = Logger.traced('M7', 'getSurpriseRoster', () => {
    if (_catalog.length === 0) return [];

    // Group by genre
    const byGenre = {};
    for (const s of _catalog) {
      const g = s.genre || '_none';
      if (!byGenre[g]) byGenre[g] = [];
      byGenre[g].push(s);
    }

    // Shuffle genres
    const genres = Object.keys(byGenre);
    for (let i = genres.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [genres[i], genres[j]] = [genres[j], genres[i]];
    }

    const picked = new Set();
    const roster = [];

    // First pass: one per genre
    for (const g of genres) {
      if (roster.length >= 10) break;
      const candidates = byGenre[g];
      const s = candidates[Math.floor(Math.random() * candidates.length)];
      roster.push(s);
      picked.add(s);
    }

    // Second pass: fill remaining slots
    if (roster.length < 10) {
      const remaining = _catalog.filter(s => !picked.has(s));
      // Shuffle remaining
      for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
      }
      for (const s of remaining) {
        if (roster.length >= 10) break;
        roster.push(s);
      }
    }

    return roster.map((s, i) => ({ slot: i + 1, url: s.url, label: s.name, url_lq: s.url_lq || '', url_hq: s.url_hq || '' }));
  });

  return { init, getStations, getGenres, filterByGenre, getSurpriseRoster };
})();
